/**
 * File Utility Functions
 * Shared utilities for file handling operations
 */

/**
 * Format bytes to human-readable file size string
 *
 * @param bytes - File size in bytes
 * @returns Formatted file size string (e.g., "2.5 MB", "512 KB")
 *
 * @example
 * ```typescript
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1048576) // "1 MB"
 * formatFileSize(2621440) // "2.5 MB"
 * ```
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Ensure we don't exceed the sizes array
  const index = Math.min(i, sizes.length - 1);
  const size = Math.round((bytes / Math.pow(k, index)) * 100) / 100;

  return `${size} ${sizes[index]}`;
}

/**
 * Check if file size exceeds maximum allowed size
 *
 * @param bytes - File size in bytes
 * @param maxSizeBytes - Maximum allowed size in bytes
 * @returns True if file exceeds maximum size
 *
 * @example
 * ```typescript
 * const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
 * isFileSizeExceeded(fileSize, MAX_SIZE)
 * ```
 */
export function isFileSizeExceeded(
  bytes: number,
  maxSizeBytes: number,
): boolean {
  return bytes > maxSizeBytes;
}

/**
 * Get file extension from filename
 *
 * @param filename - The filename to extract extension from
 * @returns File extension without the dot (e.g., "pdf", "docx")
 *
 * @example
 * ```typescript
 * getFileExtension("document.pdf") // "pdf"
 * getFileExtension("image.png") // "png"
 * ```
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === 0) {
    return '';
  }
  return filename.substring(lastDot + 1).toLowerCase();
}

/**
 * Common file size constants
 */
export const FILE_SIZE_CONSTANTS = {
  /** 1 KB in bytes */
  KB: 1024,
  /** 1 MB in bytes */
  MB: 1024 * 1024,
  /** 1 GB in bytes */
  GB: 1024 * 1024 * 1024,
  /** Maximum document size (10 MB) */
  MAX_DOCUMENT_SIZE: 10 * 1024 * 1024,
  /** Maximum image size (5 MB) */
  MAX_IMAGE_SIZE: 5 * 1024 * 1024,
  /** Maximum video size (100 MB) */
  MAX_VIDEO_SIZE: 100 * 1024 * 1024,
} as const;
